import { collectDocImageFileIds, type TiptapDoc } from '@odo/shared-ui';
import { resolveInternalImageUrls } from '../services/internalImages';
import { reportError } from '../services/errorReporting';

export type ResolvedImageUrls = Record<string, string>;

export function collectDocsImageFileIds(docs: TiptapDoc[]): string[] {
    return [...new Set(docs.flatMap(collectDocImageFileIds))];
}

// Stored docs carry expiring image URLs. Never rejects: a failure is reported and yields {}.
export async function resolveDocImages(
    patientId: string,
    docs: TiptapDoc[],
    source: string
): Promise<ResolvedImageUrls> {
    const fileIds = collectDocsImageFileIds(docs);
    if (fileIds.length === 0) return {};
    try {
        return await resolveInternalImageUrls(patientId, fileIds);
    } catch (err: unknown) {
        reportError({
            source,
            message: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined,
            url: globalThis.location?.href ?? '',
            userAgent: navigator.userAgent,
            errorType: 'upload_failure',
            metadata: { patientId }
        });
        return {};
    }
}

// Effect-friendly wrapper: calls back only with a non-empty result. Returns a cancel function.
export function resolveDocImagesInBackground(
    patientId: string,
    docs: TiptapDoc[],
    source: string,
    onResolved: (urls: ResolvedImageUrls) => void
): () => void {
    let cancelled = false;
    void resolveDocImages(patientId, docs, source).then((urls) => {
        if (!cancelled && Object.keys(urls).length > 0) onResolved(urls);
    });
    return () => {
        cancelled = true;
    };
}

interface DocNode {
    type?: string;
    attrs?: Record<string, unknown>;
    content?: DocNode[];
}

function imageFileId(node: DocNode): unknown {
    if (node.type === 'image') return node.attrs?.['data-file-id'];
    if (node.type === 'imageGridCell') return node.attrs?.fileId;
    return undefined;
}

function stripNodeSrc(node: DocNode): DocNode {
    const fileId = imageFileId(node);
    const attrs =
        typeof fileId === 'string' && fileId.length > 0 && node.attrs && 'src' in node.attrs
            ? Object.fromEntries(Object.entries(node.attrs).filter(([key]) => key !== 'src'))
            : node.attrs;
    const content = Array.isArray(node.content) ? node.content.map(stripNodeSrc) : node.content;
    return {
        ...node,
        ...(attrs !== undefined && { attrs }),
        ...(content !== undefined && { content })
    };
}

// Signed URLs expire and change per load; persisted docs keep only the file id to re-resolve.
export function stripResolvedImageSrcs(doc: TiptapDoc): TiptapDoc {
    return { ...doc, content: (doc.content as DocNode[]).map(stripNodeSrc) };
}
